// Единый реестр сущностей CRM: адрес, название, как искать.
//
// Зачем отдельный файл. Каждый сервис (дебиторка, счета, верификации) ссылается на сделки,
// компании, контакты и суда. Если каждый будет строить ссылку и поиск сам, получим пять
// разных «выбрать компанию» и пять способов собрать адрес. Поэтому описание сущности живёт
// здесь одно, а EntityLink/EntityPicker его читают.
//
// Добавить сущность = добавить строку в ENTITIES + маршрут в App.tsx. Больше нигде.

import type { Company, Contact, Deal, Vessel } from '../api/types'

export type EntityKind = 'deal' | 'company' | 'contact' | 'vessel'

/** Минимум, который нужен ссылке и выпадающему списку: чем это назвать и куда ведёт. */
export interface EntityRef {
  kind: EntityKind
  id: string
  title: string
  /** Вторая строка в выпадающем списке: ИНН, IMO, стадия — то, что помогает различить тёзок. */
  hint?: string
}

interface EntitySpec {
  /** Единственное число, с маленькой буквы: «сделка». Для подписей вида «выберите сделку». */
  one: string
  /** Раздел в меню: «Сделки». */
  many: string
  /** Адрес карточки. Единственное место, где он собирается. */
  path: (id: string | number) => string
  /** Адрес списка. */
  listPath: string
  /** Минимальная длина запроса, при которой имеет смысл искать. */
  minQuery: number
}

export const ENTITIES: Record<EntityKind, EntitySpec> = {
  deal: {
    one: 'сделку', many: 'Сделки',
    path: (id) => `/deals/${id}`, listPath: '/deals', minQuery: 2,
  },
  company: {
    one: 'компанию', many: 'Компании',
    path: (id) => `/companies/${id}`, listPath: '/companies', minQuery: 2,
  },
  contact: {
    one: 'контакт', many: 'Контакты',
    path: (id) => `/contacts/${id}`, listPath: '/contacts', minQuery: 2,
  },
  vessel: {
    // Судно в Bitrix — это контакт с фамилией «Судно». В ядре для них своя витрина
    // (GET /vessels), поэтому и адрес, и поиск отдельные.
    one: 'судно', many: 'Суда',
    path: (id) => `/vessels/${id}`, listPath: '/vessels', minQuery: 2,
  },
}

export const entityPath = (kind: EntityKind, id: string | number) => ENTITIES[kind].path(id)

/** Имя контакта из частей. Bitrix хранит их отдельно, готового поля в ответе нет. */
export function contactName(c: Contact): string {
  const parts = [c.LAST_NAME, c.NAME, c.SECOND_NAME].filter(Boolean)
  return parts.length ? parts.join(' ') : `#${c.ID}`
}

/** Судно — это контакт: у него фамилия начинается со «Судно» (регистр и приписки любые). */
export const isVesselContact = (c: Contact) =>
  (c.LAST_NAME || '').trim().toLowerCase().startsWith('судно')

/**
 * Поля контакта-судна в Bitrix. Витрина ядра `/vessels` наливает IMO/MMSI/флаг/порт
 * ровно из этих кодов, но ищет только по названию и IMO — спросить её по id нельзя.
 * Поэтому карточка судна читает контакт и разбирает поля сама, а коды лежат здесь,
 * чтобы не расползлись по экранам.
 */
export const VESSEL_LABELS: Record<string, string> = {
  imo: 'IMO', mmsi: 'MMSI', flag: 'Флаг', port: 'Порт приписки',
}

export const VESSEL_FIELDS = {
  imo: 'UF_CRM_1508500822',
  mmsi: 'UF_CRM_1545371205405',
  flag: 'UF_CRM_1552383324552',
  port: 'UF_CRM_1552383346054',
} as const

/** Поле сделки «Судно» — ссылка на контакт-судно. */
export const DEAL_VESSEL_FIELD = 'UF_CRM_1502613831'

/**
 * Название судна из контакта — так же, как его собирает витрина ядра: имя плюс отчество.
 * Отчество пропускаем, если в нём повторили «Судно»: в Bitrix так бывает, и без проверки
 * получалось бы «ТУАПСЕ Судно».
 */
export function vesselName(c: Contact): string {
  const second = (c.SECOND_NAME || '').trim()
  const parts = [(c.NAME || '').trim()]
  if (second && !second.toLowerCase().startsWith('судно')) parts.push(second)
  const name = parts.filter(Boolean).join(' ').trim()
  // Пусто — значит название лежит не там, где обычно: покажем ФИО контакта целиком,
  // это честнее, чем «#12345».
  return name || contactName(c)
}

// ─────────── приведение записей ядра к EntityRef ───────────
// Каждая сущность приходит в своём формате (сделки и компании — сырой Bitrix,
// суда — витрина ядра), а списку выбора нужен один вид.

export const dealRef = (d: Deal): EntityRef => ({
  kind: 'deal', id: String(d.ID),
  title: d.TITLE || `#${d.ID}`,
  hint: d.STAGE_ID ? String(d.STAGE_ID) : undefined,
})

export const companyRef = (c: Company): EntityRef => ({
  kind: 'company', id: String(c.ID),
  title: c.TITLE || `#${c.ID}`,
})

export const contactRef = (c: Contact): EntityRef => ({
  kind: 'contact', id: String(c.ID),
  title: contactName(c),
  hint: c.COMPANY_ID ? `компания #${c.COMPANY_ID}` : undefined,
})

export const vesselRef = (v: Vessel): EntityRef => ({
  kind: 'vessel', id: String(v.id),
  title: v.name || `#${v.id}`,
  // IMO различает суда-тёзки надёжнее порта: имена повторяются, номер уникален.
  hint: [v.imo && `IMO ${v.imo}`, v.port].filter(Boolean).join(' · ') || undefined,
})
